import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertTriangle, Loader2, Mail, Phone } from 'lucide-react';
import { publicApiFetch, getPublicFetchMessage } from '../api/public';
import { formatCalendarDate } from '../lib/calendar-date';
import { fmtCurrency } from '../lib/display-labels';

/**
 * THE RENTAL CUSTOMER'S LINK — PUBLIC, TOKEN-AS-CREDENTIAL.
 *
 * `POST /surface-rentals/orders/:orderId/portal-link` mints
 * `${FRONTEND_URL}/portal/rentals/<token>`, and until this page existed that
 * URL fell into the authenticated customer portal's `/portal/*` routes and
 * landed on /login. The reader is a rental customer with no FieldIQ account.
 *
 * Like TicketReviewPage: requests go through `publicApiFetch` (never the staff
 * token, never the 401 → /login interceptor), and the path is listed in
 * `lib/public-routes.ts` so a stale staff session in the same browser cannot
 * bounce the reader to /login before this renders.
 *
 * READ-ONLY. The API also offers `/:token/approve` and `/:token/dispute`; they
 * have never run against real traffic (see the header of the API's
 * rental-portal.routes.ts) and are not offered here.
 *
 * WHAT THE WIRE CARRIES (api#1216, api#1221): per line the asset's identifying
 * fields, the quantity, and the order's AGREED customer rate per day. The API
 * publishes `rate: null` when the only rate it could find is the asset's own
 * internal daily rate, so a null rate renders as an em dash, never a guess.
 * Purchase price, replacement value, asset rates and GPS are not sent, and this
 * page reads none of them even if they were.
 *
 * THE LOOK IS TicketReviewPage's: a white header bar with the rental company's
 * logo and name, a grey page, white rounded cards — the customer arriving from
 * either email lands on the same-looking page.
 */

interface PortalCompany {
  name?: string | null;
  logo_url?: string | null;
  phone?: string | null;
  email?: string | null;
}

interface PortalOrder {
  order_number?: string | null;
  status?: string | null;
  expected_return?: string | null;
  customer_name?: string | null;
  well_name?: string | null;
  billing_frequency?: string | null;
  /** rental_orders.start_date — a calendar day. */
  on_rent_date?: string | null;
  /** Calendar days on rent, counting the start day; computed by the API in the tenant's zone. */
  days_on_rent?: number | null;
}

interface PortalAsset {
  id?: string;
  asset_number?: string | null;
  name?: string | null;
  category?: string | null;
  make?: string | null;
  model?: string | null;
  quantity?: number | null;
  /** The order's agreed customer rate; null when none was agreed. */
  rate?: string | number | null;
  /** 'day' — billing is daily only. */
  rate_unit?: string | null;
}

interface PortalInvoice {
  id?: string;
  invoice_number?: string | null;
  total?: string | number | null;
  status?: string | null;
  invoice_date?: string | null;
}

interface PortalData {
  company: PortalCompany;
  order: PortalOrder;
  assets: PortalAsset[];
  invoices: PortalInvoice[];
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'loaded'; data: PortalData }
  // 404: the API's answer for an unknown, malformed, expired or cancelled-order token.
  | { kind: 'invalid' }
  // No response, or a 5xx: we could not find out. Not the same sentence as "invalid".
  | { kind: 'failed'; message: string };

/** Exhaustive over rental_orders.status; an unknown value reads '—', never a guess. */
const ORDER_STATUS: Record<string, string> = {
  draft: 'Draft',
  reserved: 'Reserved',
  active: 'On rent',
  closed: 'Closed',
  cancelled: 'Cancelled',
};

/** rental_invoices.status: draft, sent, paid, overdue, void, plus the two this portal's API writes. */
const INVOICE_STATUS: Record<string, string> = {
  draft: 'Draft',
  sent: 'Sent',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void',
  customer_approved: 'Approved',
  disputed: 'Disputed',
};

const BILLING_FREQUENCY: Record<string, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  monthly: 'Monthly',
  end_of_well: 'End of well',
};

const text = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null);
const label = (map: Record<string, string>, v: unknown): string => (typeof v === 'string' && map[v]) || '—';
const day = (v: unknown): string => formatCalendarDate(text(v)) ?? '—';
/** A real 0 is 0; absent is '—'. */
const count = (v: unknown): string => (typeof v === 'number' && Number.isFinite(v) ? String(v) : '—');
const daysLabel = (v: unknown): string =>
  typeof v === 'number' && Number.isFinite(v) ? `${v} ${v === 1 ? 'day' : 'days'}` : '—';
/** Only the unit the API sends; an unknown unit is not guessed at. */
const RATE_UNIT: Record<string, string> = { day: '/day' };
const rateLabel = (a: PortalAsset): string =>
  fmtCurrency(a.rate, null, 2, typeof a.rate_unit === 'string' ? RATE_UNIT[a.rate_unit] : undefined);

export async function loadRentalPortal(token: string): Promise<LoadState> {
  try {
    const res = await publicApiFetch(`/rental-portal/${encodeURIComponent(token)}`);
    if (res.status === 404) return { kind: 'invalid' };
    if (!res.ok) return { kind: 'failed', message: 'Something went wrong on our side. Please try the link again in a few minutes.' };
    const body = await res.json();
    const data = body && 'data' in body ? body.data : body;
    if (!data || typeof data !== 'object' || !data.order) {
      return { kind: 'failed', message: 'Something went wrong on our side. Please try the link again in a few minutes.' };
    }
    return {
      kind: 'loaded',
      data: {
        company: data.company && typeof data.company === 'object' ? data.company : {},
        order: data.order,
        assets: Array.isArray(data.assets) ? data.assets : [],
        invoices: Array.isArray(data.invoices) ? data.invoices : [],
      },
    };
  } catch (err) {
    return { kind: 'failed', message: getPublicFetchMessage(err, 'We could not load this rental. Check your connection and try again.') };
  }
}

const Spec: React.FC<{ term: string; value: string; testId?: string }> = ({ term, value, testId }) => (
  <div>
    <dt className="text-xs uppercase tracking-wide text-gray-500">{term}</dt>
    <dd className="mt-0.5 text-sm font-medium text-gray-900" data-testid={testId}>{value}</dd>
  </div>
);

const Shell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="min-h-screen bg-gray-50 px-4 py-8">
    <div className="mx-auto w-full max-w-5xl space-y-4">{children}</div>
  </div>
);

/** The rental company — TicketReviewPage's header bar, plus how to reach them. */
const CompanyHeader: React.FC<{ company: PortalCompany; orderNumber: string }> = ({ company, orderNumber }) => {
  const phone = text(company.phone);
  const email = text(company.email);
  return (
    <div className="bg-white border-b px-6 py-4" data-testid="rental-portal-company">
      <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-3">
        {text(company.logo_url) && <img src={company.logo_url as string} alt="" className="h-8" data-testid="rental-portal-logo" />}
        <div className="flex-1 min-w-0">
          <h1 className="font-bold text-gray-900">{text(company.name) ?? '—'}</h1>
          <p className="text-sm text-gray-500">Rental Order</p>
          <p className="text-sm font-semibold text-gray-700" data-testid="rental-portal-order-number">{orderNumber}</p>
        </div>
        {(phone || email) && (
          <div className="flex flex-col items-start gap-1 text-sm sm:items-end">
            {phone && (
              <a href={`tel:${phone.replace(/[^\d+]/g, '')}`} className="inline-flex items-center gap-1.5 text-gray-700 hover:text-gray-900">
                <Phone className="h-4 w-4 text-gray-400" aria-hidden="true" /> {phone}
              </a>
            )}
            {email && (
              <a href={`mailto:${email}`} className="inline-flex items-center gap-1.5 text-gray-700 hover:text-gray-900">
                <Mail className="h-4 w-4 text-gray-400" aria-hidden="true" /> {email}
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const Card: React.FC<{ title: string; id: string; children: React.ReactNode }> = ({ title, id, children }) => (
  <section className="bg-white rounded-xl shadow-sm border" aria-labelledby={id}>
    <h2 id={id} className="border-b px-5 py-3 font-bold text-gray-900">{title}</h2>
    {children}
  </section>
);

const RentalPortalPage: React.FC = () => {
  const { token = '' } = useParams<{ token: string }>();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  useEffect(() => {
    let live = true;
    setState({ kind: 'loading' });
    loadRentalPortal(token).then((s) => { if (live) setState(s); });
    return () => { live = false; };
  }, [token]);

  if (state.kind === 'loading') {
    return (
      <Shell>
        <div className="flex items-center justify-center gap-2 py-16 text-gray-500" role="status">
          <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading your rental…
        </div>
      </Shell>
    );
  }

  if (state.kind === 'invalid' || state.kind === 'failed') {
    return (
      <Shell>
        <div className="bg-white rounded-xl shadow-sm border p-6 text-center" role="alert" data-testid={`rental-portal-${state.kind}`}>
          <AlertTriangle className="mx-auto h-8 w-8 text-amber-500" aria-hidden="true" />
          <h1 className="mt-3 text-lg font-semibold text-gray-900">
            {state.kind === 'invalid' ? 'This link has expired or is no longer valid' : 'We could not load this rental'}
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {state.kind === 'invalid'
              ? 'Ask your rental company to send you a new link.'
              : state.message}
          </p>
        </div>
      </Shell>
    );
  }

  const { company, order, assets, invoices } = state.data;
  return (
    <div className="min-h-screen bg-gray-50" data-testid="rental-portal-page">
      <CompanyHeader company={company} orderNumber={text(order.order_number) ?? '—'} />

      <div className="max-w-5xl mx-auto px-6 py-4 space-y-4">
        <Card title="Order" id="rental-portal-order">
          <dl className="grid grid-cols-2 gap-4 p-5 sm:grid-cols-4">
            <Spec term="Customer" value={text(order.customer_name) ?? '—'} />
            <Spec term="Well" value={text(order.well_name) ?? '—'} />
            <Spec term="Status" value={label(ORDER_STATUS, order.status)} />
            <Spec term="Billing" value={label(BILLING_FREQUENCY, order.billing_frequency)} />
            <Spec term="On-rent date" value={day(order.on_rent_date)} testId="rental-portal-on-rent-date" />
            <Spec term="Days on rent" value={daysLabel(order.days_on_rent)} testId="rental-portal-days-on-rent" />
            <Spec term="Expected return" value={day(order.expected_return)} />
          </dl>
        </Card>

        <Card title="Equipment on this order" id="rental-portal-assets">
          {assets.length === 0 ? (
            <p className="px-5 py-4 text-sm text-gray-600">No equipment on this order.</p>
          ) : (
            <ul className="divide-y">
              {assets.map((a, i) => {
                const makeModel = [text(a.make), text(a.model)].filter(Boolean).join(' ');
                return (
                  <li key={a.id ?? i} className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1 px-5 py-3 text-sm" data-testid="rental-portal-line">
                    <div className="min-w-0">
                      <p className="font-medium text-gray-900">
                        {text(a.name) ?? '—'}
                        {makeModel && <span className="font-normal text-gray-500"> · {makeModel}</span>}
                      </p>
                      <p className="font-mono text-xs text-gray-500">{text(a.asset_number) ?? '—'}</p>
                    </div>
                    <dl className="flex gap-6 text-right">
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-gray-500">Qty</dt>
                        <dd className="font-medium text-gray-900" data-testid="rental-portal-line-qty">{count(a.quantity)}</dd>
                      </div>
                      <div>
                        <dt className="text-xs uppercase tracking-wide text-gray-500">Rate</dt>
                        <dd className="font-medium text-gray-900" data-testid="rental-portal-line-rate">{rateLabel(a)}</dd>
                      </div>
                    </dl>
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card title="Invoices" id="rental-portal-invoices">
          {invoices.length === 0 ? (
            <p className="px-5 py-4 text-sm text-gray-600">No invoices on this order yet.</p>
          ) : (
            <ul className="divide-y">
              {invoices.map((inv, i) => (
                <li key={inv.id ?? i} className="flex flex-wrap items-baseline justify-between gap-2 px-5 py-3 text-sm">
                  <span className="text-gray-900">
                    {text(inv.invoice_number) ?? '—'}
                    <span className="text-gray-500"> · {day(inv.invoice_date)} · {label(INVOICE_STATUS, inv.status)}</span>
                  </span>
                  <span className="font-medium text-gray-900">{fmtCurrency(inv.total)}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};

export default RentalPortalPage;
